import { useState, type FormEvent } from "react";

import { fetchDemoAskChart, type DemoV3AskChartResult } from "../../../api/demoV3";

/**
 * Ask the chart (VERTICAL_DEMOS_V2 D4): query the GP surrogate behind the demo
 * database at an off-sample condition and get CL / CD / L-over-D as mean ± σ.
 * The σ is rendered prominently — the answer knows where the data is thin.
 *
 * Inputs are driven by widget state ({inputs: [{key, label, value}]}) with a
 * sensible default per project: the CFD demo (…0001) asks (alpha, rpm, beta);
 * the S&C demos ask (alpha, beta, elevator).
 */

const CFD_PROJECT_ID = "c0ffee00-0000-4000-8000-000000000001";

export interface AskChartInputSpec {
  key: string;
  label: string;
  value: number;
}

function defaultInputs(projectId: string, state?: Record<string, unknown>): AskChartInputSpec[] {
  const fromState = Array.isArray(state?.inputs) ? (state?.inputs as unknown[]) : null;
  if (fromState && fromState.length > 0) {
    const parsed = fromState
      .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object")
      .map((item) => ({
        key: String(item.key ?? ""),
        label: String(item.label ?? item.key ?? ""),
        value: typeof item.value === "number" ? item.value : 0,
      }))
      .filter((item) => item.key);
    if (parsed.length > 0) return parsed;
  }
  if (projectId === CFD_PROJECT_ID) {
    return [
      { key: "alpha", label: "α (deg)", value: 7.3 },
      { key: "rpm", label: "prop rpm", value: 4500 },
      { key: "beta", label: "β (deg)", value: 2.0 },
    ];
  }
  return [
    { key: "alpha", label: "α (deg)", value: 3.7 },
    { key: "beta", label: "β (deg)", value: 1.2 },
    { key: "elevator", label: "δe (deg)", value: -2.5 },
  ];
}

function fmtMean(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  return value.toFixed(abs >= 100 ? 1 : abs >= 10 ? 2 : abs >= 1 ? 3 : 4);
}

function fmtStd(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return value >= 0.01 ? value.toFixed(3) : value.toExponential(1);
}

export function AskChartCard({
  projectId,
  state,
}: {
  projectId: string;
  branch?: string;
  state?: Record<string, unknown>;
}) {
  const [inputs, setInputs] = useState<AskChartInputSpec[]>(() => defaultInputs(projectId, state));
  const [result, setResult] = useState<DemoV3AskChartResult | null>(null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = (event?: FormEvent) => {
    event?.preventDefault();
    const query = Object.fromEntries(inputs.map((input) => [input.key, input.value]));
    setAsking(true);
    setError(null);
    fetchDemoAskChart(projectId, query)
      .then(setResult)
      .catch((err: Error) => { setResult(null); setError(err.message); })
      .finally(() => setAsking(false));
  };

  const outputs = result
    ? ([
        { key: "CL", label: "CL", out: result.outputs.CL },
        { key: "CD", label: "CD", out: result.outputs.CD },
        { key: "L_D", label: "L/D", out: result.outputs.L_D },
      ] as const)
    : null;

  return (
    <form className="v5-demo-card v5-ask-chart" data-tour="v5-ask-chart" data-testid="v5-ask-chart" onSubmit={ask}>
      <div className="v5-ask-chart-inputs">
        {inputs.map((input, index) => (
          <label key={input.key} className="v5-ask-chart-field">
            <span>{input.label}</span>
            <input
              type="number"
              step="any"
              value={input.value}
              data-testid={`v5-ask-chart-input-${input.key}`}
              onChange={(event) => {
                const value = Number(event.target.value);
                setInputs((current) => current.map((item, i) => (i === index ? { ...item, value } : item)));
              }}
            />
          </label>
        ))}
        <button className="v5-primary-button v5-ask-chart-submit" type="submit" disabled={asking} data-testid="v5-ask-chart-ask">
          {asking ? "Asking…" : "Ask"}
        </button>
      </div>

      {error ? <div className="v5-demo-hint is-error" data-testid="v5-ask-chart-error">{error}</div> : null}

      {outputs ? (
        <div className="v5-ask-chart-readouts" data-testid="v5-ask-chart-readouts">
          {outputs.map(({ key, label, out }) => (
            <div key={key} className="v5-ask-chart-readout">
              <span className="v5-ask-chart-quantity">{label}</span>
              <strong className="v5-ask-chart-mean">{fmtMean(out.mean)}</strong>
              <span className="v5-ask-chart-sigma" title="1σ from the GP surrogate — grows where the data is thin">
                ± {fmtStd(out.std)}
              </span>
            </div>
          ))}
          <footer className="v5-demo-footnote">
            {result?.n_training} training points · σ grows where the data is thin
          </footer>
        </div>
      ) : !error ? (
        <div className="v5-demo-hint">
          Ask the chart a question it wasn't plotted to answer — an off-sample prediction with uncertainty.
        </div>
      ) : null}
    </form>
  );
}
